import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { invalidate } from "@react-three/fiber";
import { useSelector } from "react-redux";
import * as THREE from "three";
import { formatHeatmapValue, getPolygonArea, getPolygonPoints } from "../utils";
import { computeCanvasLabelFontPx } from "./heatmapLabelSizing";
import { DEFAULT_HEATMAP_LABEL_DIGITS } from "./heatmapLabelLayers";

const MAX_TEXTURE_SIZE = 2048;
const PLANE_LIFT = 0.12;

function computePlanBounds(items, getPosition, getLabelPolygon) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const polygon = getLabelPolygon?.(item);
    const points = polygon ? getPolygonPoints(polygon) : null;

    if (points?.length) {
      for (let j = 0; j < points.length; j += 1) {
        const [x, y, z] = points[j];
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
        maxZ = Math.max(maxZ, z);
      }
    } else {
      const [x, y, z] = getPosition(item);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      maxZ = Math.max(maxZ, z);
    }
  }

  if (!Number.isFinite(minX)) return null;

  const spanX = Math.max(maxX - minX, 1);
  const spanY = Math.max(maxY - minY, 1);
  const pad = Math.max(spanX, spanY) * 0.02 + 0.5;

  return {
    minX: minX - pad,
    maxX: maxX + pad,
    minY: minY - pad,
    maxY: maxY + pad,
    spanX: spanX + pad * 2,
    spanY: spanY + pad * 2,
    planeZ: maxZ + PLANE_LIFT,
  };
}

function createCanvasSize(spanX, spanY) {
  const aspect = spanX / spanY;
  if (aspect >= 1) {
    return {
      width: MAX_TEXTURE_SIZE,
      height: Math.max(64, Math.round(MAX_TEXTURE_SIZE / aspect)),
    };
  }
  return {
    width: Math.max(64, Math.round(MAX_TEXTURE_SIZE * aspect)),
    height: MAX_TEXTURE_SIZE,
  };
}

function paintLabels({
  canvas,
  ctx,
  texture,
  items,
  bounds,
  getPosition,
  getValue,
  getVisible,
  getLabelPolygon,
  fontScale,
  adaptive,
  sizePercent,
  digits,
}) {
  const { width, height } = canvas;
  ctx.clearRect(0, 0, width, height);

  if (!items.length) {
    texture.needsUpdate = true;
    return;
  }

  const pxPerUnit = width / bounds.spanX;

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#111111";
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (getVisible && !getVisible(item)) continue;

    const rawValue = getValue(item);
    const text = formatHeatmapValue(rawValue, digits);
    if (!text) continue;

    const [lx, ly] = getPosition(item);
    const polygon = getLabelPolygon?.(item);
    const area = polygon ? getPolygonArea(polygon) : 4;
    const fontSize = computeCanvasLabelFontPx({
      adaptive,
      sizePercent,
      fontScale,
      area,
      pxPerUnit,
    });

    const cx = ((lx - bounds.minX) / bounds.spanX) * width;
    const cy = height - ((ly - bounds.minY) / bounds.spanY) * height;

    ctx.font = `700 ${fontSize}px system-ui, sans-serif`;
    ctx.lineWidth = Math.max(2, fontSize * 0.18);
    ctx.strokeStyle = "rgba(255,255,255,0.95)";
    ctx.lineJoin = "round";
    ctx.miterLimit = 2;
    ctx.strokeText(text, cx, cy);
    ctx.fillText(text, cx, cy);
  }

  texture.needsUpdate = true;
}

function createSurface(canvasSize) {
  const canvas = document.createElement("canvas");
  canvas.width = canvasSize.width;
  canvas.height = canvasSize.height;
  const ctx = canvas.getContext("2d");
  const texture = new THREE.CanvasTexture(canvas);
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 8;
  texture.colorSpace = THREE.SRGBColorSpace;
  return { canvas, ctx, texture };
}

export function ValueLabelsGroup({
  items = [],
  getPosition,
  getValue,
  getVisible,
  getLabelPolygon,
  visible = true,
  fontScale = 1,
  layerKey,
}) {
  const labelsEnabled = useSelector(
    (state) => state.sceneVar.heatmapLabelsEnabled,
  );
  const adaptive = useSelector((state) => state.sceneVar.heatmapLabelsAdaptive);
  const sizePercent = useSelector(
    (state) => state.sceneVar.heatmapLabelsSizePercent,
  );
  const digits = useSelector(
    (state) =>
      state.sceneVar.heatmapLabelDigitsByLayer?.[layerKey] ??
      DEFAULT_HEATMAP_LABEL_DIGITS,
  );
  const shouldRender = visible && labelsEnabled && items.length > 0;

  const configRef = useRef({
    getPosition,
    getValue,
    getVisible,
    getLabelPolygon,
    fontScale,
    adaptive,
    sizePercent,
    digits,
  });
  configRef.current = {
    getPosition,
    getValue,
    getVisible,
    getLabelPolygon,
    fontScale,
    adaptive,
    sizePercent,
    digits,
  };

  const bounds = useMemo(() => {
    if (!shouldRender) return null;
    const config = configRef.current;
    return computePlanBounds(items, config.getPosition, config.getLabelPolygon);
  }, [shouldRender, items]);

  const canvasSize = useMemo(() => {
    if (!bounds) return null;
    return createCanvasSize(bounds.spanX, bounds.spanY);
  }, [bounds?.spanX, bounds?.spanY]);

  const surface = useMemo(() => {
    if (!shouldRender || !canvasSize) return null;
    return createSurface(canvasSize);
  }, [shouldRender, canvasSize?.width, canvasSize?.height]);

  useEffect(() => {
    return () => {
      surface?.texture.dispose();
    };
  }, [surface]);

  useLayoutEffect(() => {
    if (!shouldRender || !surface || !bounds) return;

    const config = configRef.current;
    paintLabels({
      canvas: surface.canvas,
      ctx: surface.ctx,
      texture: surface.texture,
      items,
      bounds,
      getPosition: config.getPosition,
      getValue: config.getValue,
      getVisible: config.getVisible,
      getLabelPolygon: config.getLabelPolygon,
      fontScale: config.fontScale,
      adaptive: config.adaptive,
      sizePercent: config.sizePercent,
      digits: config.digits,
    });
    invalidate();
  });

  if (!shouldRender || !bounds || !surface) return null;

  return (
    <mesh
      position={[
        (bounds.minX + bounds.maxX) / 2,
        (bounds.minY + bounds.maxY) / 2,
        bounds.planeZ,
      ]}
      renderOrder={10}
    >
      <planeGeometry args={[bounds.spanX, bounds.spanY]} />
      <meshBasicMaterial
        map={surface.texture}
        transparent
        depthWrite={false}
        depthTest
        side={THREE.DoubleSide}
        toneMapped={false}
      />
    </mesh>
  );
}
